import React from 'react';
import { Gauge, Rabbit, Turtle } from 'lucide-react';
import { CONFUSION_GRADIENT, confusionColor, confusionLabel } from '@/components/ConfusionMeter';
import { cn } from '@/lib/utils';

export interface PaceSummary {
  faster: number;
  slower: number;
  count: number;
}

export interface ConfusionSummary {
  session_id: string;
  count: number;
  average: number | null;
  buckets: number[];
  pace?: PaceSummary | null;
  updated_at: string;
}

const BUCKET_LABELS = ['Lost', 'Confused', 'A bit lost', 'Mostly', 'Following'];

interface ClassPulseProps {
  summary: ConfusionSummary | null;
  className?: string;
}

/** Professor-side view of the confusion meter: class average on a gradient, plus the spread. */
export function ClassPulse({ summary, className }: ClassPulseProps) {
  const count = summary?.count ?? 0;
  const average = summary?.average ?? null;
  const buckets = summary?.buckets ?? [0, 0, 0, 0, 0];
  const maxBucket = Math.max(1, ...buckets);
  const pace = summary?.pace ?? null;

  return (
    <section className={cn('rounded-2xl border bg-card p-5 shadow-soft sm:p-6', className)} aria-live="polite">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <Gauge className="h-4 w-4 text-muted-foreground" aria-hidden />
          <h2 className="text-base font-semibold">Class pulse</h2>
        </div>
        <span className="text-sm text-muted-foreground">
          {count === 0 ? 'No responses yet' : `${count} ${count === 1 ? 'student' : 'students'} responding`}
        </span>
      </div>

      {count === 0 || average == null ? (
        <p className="mt-3 text-sm text-muted-foreground">
          Students can slide a meter on their screen to say how well they're following. You'll see the
          class average and spread here as they respond.
        </p>
      ) : (
        <div className="mt-5 grid gap-6 sm:grid-cols-[1fr_auto] sm:items-end">
          <div>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span
                className="text-2xl font-bold leading-tight tracking-tight sm:text-3xl"
                style={{ color: confusionColor(average) }}
              >
                {confusionLabel(average)}
              </span>
              <span className="whitespace-nowrap text-sm tabular-nums text-muted-foreground">
                avg {Math.round(average)} / 100
              </span>
            </div>
            <div className="relative mt-4 h-3 rounded-full" style={{ backgroundImage: CONFUSION_GRADIENT }}>
              <span
                className="absolute top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-card transition-[left] duration-500"
                style={{
                  left: `calc(${average}% + ${(12 - average * 0.24).toFixed(1)}px)`,
                  backgroundColor: confusionColor(average),
                }}
                aria-hidden
              />
            </div>
            <div className="mt-2 flex justify-between text-xs font-medium text-muted-foreground">
              <span>Lost</span>
              <span>Following along</span>
            </div>
          </div>

          <div className="flex items-end justify-between gap-2 sm:justify-start" role="img" aria-label="Distribution of students from lost to following along">
            {buckets.map((n, i) => {
              const centre = (i + 0.5) * (100 / buckets.length);
              return (
                <div key={i} className="flex w-full max-w-[44px] flex-col items-center gap-1.5 sm:w-11">
                  <span className="text-xs font-semibold tabular-nums text-muted-foreground">{n}</span>
                  <div className="flex h-16 w-full items-end rounded-md bg-muted">
                    <div
                      className="w-full rounded-md transition-[height] duration-500"
                      style={{ height: `${(n / maxBucket) * 100}%`, backgroundColor: confusionColor(centre) }}
                    />
                  </div>
                  <span className="text-[10px] leading-tight text-muted-foreground">{BUCKET_LABELS[i]}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {pace && pace.count > 0 && (
        <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-t pt-4 text-sm">
          <span className="text-muted-foreground">Pace requests</span>
          {pace.slower > 0 && (
            <span className="inline-flex items-center gap-1.5 font-medium">
              <Turtle className="h-4 w-4 text-muted-foreground" aria-hidden />
              {pace.slower} {pace.slower === 1 ? 'wants' : 'want'} you to slow down
            </span>
          )}
          {pace.faster > 0 && (
            <span className="inline-flex items-center gap-1.5 font-medium">
              <Rabbit className="h-4 w-4 text-muted-foreground" aria-hidden />
              {pace.faster} {pace.faster === 1 ? 'wants' : 'want'} you to speed up
            </span>
          )}
        </div>
      )}
    </section>
  );
}
